import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { AppSprite, hasSprite, hasEmblem, iconSrc } from "./AppSprites";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE COMMAND BRIDGE — StudoX Core's device-adaptive launcher shell.
 * ═══════════════════════════════════════════════════════════════════════════
 * Not a scrolling card list: an operating-deck. Three genuinely different
 * layouts, chosen live by viewport:
 *
 *   🖥️ desktop/wide  — fixed full-viewport bridge: the 8 "organ" categories as
 *                       a glowing spine on the left, a kinetic app stage on the
 *                       right, a ⌘K command palette, full keyboard control
 *                       (1–8 switch organs, ⌘K → type → ↵ launches anything).
 *   📱 mobile        — a premium phone-launcher: app-icon grid, swipeable
 *                       bottom category dock, bottom-sheet app details.
 *   📲 tablet        — the bridge with an icon-only spine.
 *
 * Pure presentation: ALL auth/SSO/launch/trial logic stays in StudoXCore and
 * arrives via props. Children's-data flows are untouched by design.
 */

/* ── die 8 Welten des Cores ───────────────────────────────────────────────── */
/**
 * DIE WELTEN (v3) — Kategorien, bei denen man sofort checkt, was man bekommt.
 * Die Organ-Metapher (Herz/Hirn/Motor …) ist komplett Geschichte: jede Welt
 * ist nach ihrem NUTZEN benannt, alle Apps liegen als gleich große Gate-Kacheln
 * darin. "dunkel" ist die bewusst separierte dunkle Seite (eigenes Styling).
 */
export const CATEGORIES = [
  { id: "lernen",  label: "Lernen & Leveln",  sub: "XP, Missionen & Fortschritt",        short: "Leveln",  icon: "🚀", color: "#8b5cf6", appIds: ["flow", "quest", "cinema", "futurecity", "step"] },
  { id: "leben",   label: "Fürs echte Leben", sub: "Skills, die Schule nicht lehrt",     short: "Leben",   icon: "🧭", color: "#3ddc97", appIds: ["adulting", "startup", "looksmax"] },
  { id: "machen",  label: "Selber machen",    sub: "Design, Musik & deine Stimme",       short: "Machen",  icon: "🎨", color: "#f43f5e", appIds: ["create", "lyrics", "mic"] },
  { id: "vibes",   label: "Sound & Kino",     sub: "Musik, Cinematics & Erlebnisse",     short: "Vibes",   icon: "🎧", color: "#a855f7", appIds: ["music", "phonk", "rapvid"] },
  { id: "leute",   label: "Deine Leute",      sub: "Freunde, Clubs & Events",            short: "Leute",   icon: "🤝", color: "#ec4899", appIds: ["meet", "clubs", "events", "discord", "invite"] },
  { id: "halt",    label: "Hilfe & Halt",     sub: "Notfallhilfe, Support & Status",     short: "Halt",    icon: "💗", color: "#fb7185", appIds: ["help", "support", "status"] },
  { id: "welt",    label: "Hinter StudoX",    sub: "Mission, Info & Investoren",         short: "StudoX",  icon: "🌍", color: "#fbbf24", appIds: ["hub", "info", "impact", "invest"] },
  { id: "dunkel",  label: "Die dunkle Seite", sub: "Ernste Wahrheiten · beschränkt",     short: "Dunkel",  icon: "🕯️", color: "#e5273a", appIds: ["dossier", "horror"], dark: true },
];

const LINKS = [
  { label: "flow",   href: "https://flow.studox.eu",   color: "#8b5cf6" },
  { label: "meet",   href: "https://meet.studox.eu",   color: "#7c3aed" },
  { label: "clubs",  href: "https://clubs.studox.eu",  color: "#10b981" },
  { label: "lyrics", href: "https://lyrics.studox.eu", color: "#ec4899" },
  { label: "info",   href: "https://studox.info",      color: "#a78bfa" },
  { label: "impact", href: "https://studox.org",       color: "#f87171" },
  { label: "help",   href: "https://help.studox.eu",   color: "#f43f5e" },
];

/* ── live viewport class ──────────────────────────────────────────────────── */
function useViewport() {
  const calc = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1280;
    if (w < 768) return "mobile";
    if (w < 1200) return "tablet";
    if (w < 1680) return "desktop";
    return "wide";
  };
  const [vp, setVp] = useState(calc);
  useEffect(() => {
    const onR = () => setVp(calc());
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return vp;
}

/* ── shared: render an app's mark (SVG component or emoji fallback) ───────── */
function AppGlyph({ app, size = 56, glow = false }) {
  const Mark = app.Mark;
  if (Mark) return <Mark size={size} glow={glow} animated={glow} dimmed={app.comingSoon && !glow} />;
  return (
    <div className="grid place-items-center rounded-2xl" style={{ width: size, height: size, background: `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`, fontSize: size * 0.48 }}>
      <span>{app.emoji || "✦"}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ⌘K COMMAND PALETTE — launch anything in under a second
   ═══════════════════════════════════════════════════════════════════════════ */
function CommandPalette({ apps, onLaunch, onClose }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const catOf = (id) => CATEGORIES.find((c) => c.appIds.includes(id));
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = apps.filter((a) => {
      if (!needle) return true;
      const cat = catOf(a.id);
      return (
        a.name.toLowerCase().includes(needle) ||
        a.tagline.toLowerCase().includes(needle) ||
        (cat && (cat.label.toLowerCase().includes(needle) || cat.sub.toLowerCase().includes(needle)))
      );
    });
    return pool.slice(0, 8);
  }, [q, apps]);

  useEffect(() => { setSel(0); }, [q]);

  const fire = useCallback((app) => { if (!app) return; onClose(); onLaunch(app); }, [onClose, onLaunch]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); fire(results[sel]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-start justify-center px-4 pt-[10vh]" style={{ background: "rgba(2,2,10,0.72)", backdropFilter: "blur(14px)" }} onClick={onClose}>
      <div className="palette-in w-full max-w-xl rounded-3xl overflow-hidden border" style={{ background: "rgba(10,9,26,0.97)", borderColor: "rgba(139,92,246,0.4)", boxShadow: "0 0 80px rgba(139,92,246,0.28), 0 40px 100px rgba(0,0,0,0.8)" }} onClick={(e) => e.stopPropagation()}>
        <div className="absolute-none flex items-center gap-3 px-5 py-4 border-b border-white/8">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#a78bfa" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="App suchen und sofort starten…"
            className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder-gray-600 font-semibold"
          />
          <span className="text-[10px] font-black text-gray-600 border border-white/10 rounded-md px-1.5 py-0.5">ESC</span>
        </div>
        <div className="max-h-[46vh] overflow-y-auto p-2">
          {results.length === 0 && <p className="text-center text-sm text-gray-600 py-8">Nichts gefunden für „{q}"</p>}
          {results.map((app, i) => {
            const cat = catOf(app.id);
            const active = i === sel;
            return (
              <button
                key={app.id}
                onMouseEnter={() => setSel(i)}
                onClick={() => fire(app)}
                className="w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-2xl text-left transition-colors"
                style={{ background: active ? "rgba(139,92,246,0.16)" : "transparent", border: active ? "1px solid rgba(139,92,246,0.35)" : "1px solid transparent" }}
              >
                <div className="shrink-0 grid place-items-center" style={{ width: 38, height: 38 }}>
                  {iconSrc(app.id) ? (
                    <img src={iconSrc(app.id)} alt="" className="object-contain" style={{ width: 38, height: 38 }} loading="lazy" />
                  ) : (
                    <AppGlyph app={app} size={38} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-white truncate">{app.name} {app.comingSoon && <span className="text-[9px] font-black text-amber-400 ml-1">BALD</span>}</p>
                  <p className="text-[11px] text-gray-500 truncate">{app.tagline}</p>
                </div>
                {cat && (
                  <span className="hidden sm:inline text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg shrink-0" style={{ color: cat.color, background: `${cat.color}14`, border: `1px solid ${cat.color}30` }}>
                    {cat.icon} {cat.short}
                  </span>
                )}
                {active && <span className="text-[10px] font-black text-violet-300 shrink-0">↵</span>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 px-5 py-2.5 border-t border-white/8 text-[9.5px] font-bold text-gray-600 uppercase tracking-widest">
          <span>↑↓ Navigieren</span><span>↵ Starten</span><span>Esc Schließen</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP / TABLET / WIDE — the bridge
   ═══════════════════════════════════════════════════════════════════════════ */
function SpineItem({ cat, index, active, compact, count, onPick }) {
  return (
    <button
      onClick={onPick}
      title={compact ? `${cat.label} — ${cat.sub}` : undefined}
      className="relative w-full flex items-center gap-3 rounded-2xl transition-all duration-200 group text-left"
      style={{
        padding: compact ? "10px 0" : "10px 12px",
        justifyContent: compact ? "center" : "flex-start",
        background: active ? `linear-gradient(90deg, ${cat.color}1f, transparent)` : "transparent",
      }}
    >
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-full transition-all duration-300" style={{ height: active ? 30 : 0, background: `linear-gradient(180deg, ${cat.color}, transparent)`, boxShadow: active ? `0 0 12px ${cat.color}` : "none", opacity: active ? 1 : 0 }} />
      <span
        className="grid place-items-center rounded-xl text-lg shrink-0 transition-all duration-200 group-hover:scale-110"
        style={{ width: 40, height: 40, background: active ? `${cat.color}26` : "rgba(255,255,255,0.04)", border: `1px solid ${active ? `${cat.color}55` : "rgba(255,255,255,0.07)"}`, filter: active ? `drop-shadow(0 0 10px ${cat.color}66)` : "none" }}
      >
        {cat.icon}
      </span>
      {!compact && (
        <span className="flex-1 min-w-0">
          <span className="block text-[12px] font-black leading-tight truncate transition-colors" style={{ color: active ? "#fff" : "rgba(255,255,255,0.55)" }}>{cat.label}</span>
          <span className="block text-[10px] font-bold uppercase tracking-wider leading-tight mt-0.5 truncate" style={{ color: active ? `${cat.color}dd` : "rgba(255,255,255,0.28)" }}>{cat.sub}</span>
        </span>
      )}
      {!compact && (
        <span className="shrink-0 text-[9px] font-black rounded-md px-1.5 py-0.5 transition-colors" style={{ color: active ? cat.color : "rgba(255,255,255,0.25)", background: active ? `${cat.color}18` : "rgba(255,255,255,0.04)" }}>
          {count}
        </span>
      )}
      {!compact && <kbd className="absolute right-2 -top-1 text-[8px] font-black text-white/0 group-hover:text-white/25 transition-colors">{index + 1}</kbd>}
    </button>
  );
}

/**
 * GATE-KACHEL (v3) — EINE Kachelform für ALLE Apps: quadratisch, gleich groß,
 * volumetrisch. Kein Hero/Compact-Gefälle mehr — Wichtigkeit entscheidet der
 * Nutzer, nicht das Layout. Aufbau: Conic-Energie-Rahmen → dunkles Glas mit
 * Akzent-Bloom → schwebendes Emblem → Name/Tagline → Status-Chips.
 */
function GateTile({ app, index = 0, onLaunch }) {
  const [hover, setHover] = useState(false);
  const alive = hasSprite(app.id);
  const awake = hover && alive;
  return (
    <div
      className="gate-ring rounded-[26px] transition-transform duration-300 hover:-translate-y-1.5"
      style={{
        padding: 1.4,
        background: `conic-gradient(from ${140 + index * 40}deg, ${app.accentA}66, ${app.accentB}22, rgba(255,255,255,0.05), ${app.accentA}66)`,
        animation: `gate-in .5s cubic-bezier(0.16,1,0.3,1) ${index * 0.045}s both`,
      }}
    >
      <button
        onClick={onLaunch}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="card-sheen gate-tile group relative flex aspect-square w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-[25px] p-4 text-center"
        style={{
          background: `radial-gradient(ellipse 90% 55% at 50% 0%, ${app.accentA}1e 0%, transparent 55%), radial-gradient(ellipse 80% 60% at 50% 115%, ${app.accentB}14 0%, transparent 60%), rgb(8,8,18)`,
          boxShadow: `0 14px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.07)`,
          opacity: app.comingSoon ? 0.72 : 1,
        }}
      >
        {/* status chips — top corners */}
        <div className="absolute left-2.5 top-2.5 z-20 flex gap-1">
          {app.comingSoon && <span className="rounded-md px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-amber-300" style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.4)" }}>Bald</span>}
          {app.prototype && <span className="rounded-md px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider" style={{ color: app.accentA, background: `${app.accentA}14`, border: `1px solid ${app.accentA}45` }}>Beta</span>}
          {app.restricted && <span className="rounded-md px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-red-300" style={{ background: "rgba(229,39,58,0.12)", border: "1px solid rgba(229,39,58,0.45)" }}>16+</span>}
        </div>
        {app.external && (
          <span className="absolute right-2.5 top-2.5 z-20 text-[10px] font-black opacity-30 transition-opacity group-hover:opacity-80" style={{ color: app.accentA }}>↗</span>
        )}

        {/* DAS ERWACHEN — Emblem in Ruhe, lebender Charakter beim Hover */}
        <div className="relative grid w-full place-items-center" style={{ height: 118 }}>
          {/* spotlight, das mit dem Charakter aufglüht */}
          <div
            className="pointer-events-none absolute inset-0 transition-opacity duration-500"
            style={{ opacity: awake ? 1 : 0, background: `radial-gradient(ellipse 62% 46% at 50% 58%, ${app.accentA}30, transparent 70%)` }}
          />
          <div
            className="gate-glyph transition-all duration-300"
            style={{
              filter: `drop-shadow(0 10px 22px ${app.accentA}45)`,
              opacity: awake ? 0 : 1,
              transform: awake ? "scale(0.62)" : hover ? "scale(1.08)" : "none",
            }}
          >
            {hasEmblem(app.id) ? (
              <AppSprite appId={app.id} variant="emblem" size={94} playing={!awake} warm fps={11} />
            ) : (
              <AppGlyph app={app} size={82} glow />
            )}
          </div>
          {alive && (
            <div
              className="pointer-events-none absolute inset-0 grid place-items-center transition-all duration-300"
              style={{ opacity: awake ? 1 : 0, transform: awake ? "scale(1)" : "scale(0.8)" }}
            >
              <AppSprite appId={app.id} size={124} playing={awake} warm={hover} style={{ filter: `drop-shadow(0 14px 26px ${app.accentA}55)` }} />
              {/* Boden-Glow unter dem Wesen */}
              <div className="absolute bottom-0 h-4 w-3/5 rounded-full blur-md" style={{ background: `radial-gradient(ellipse, ${app.accentA}55, transparent 70%)`, opacity: awake ? 1 : 0 }} />
            </div>
          )}
        </div>

        {/* name + tagline */}
        <div className="min-w-0 w-full">
          <p className="truncate text-[14px] font-black leading-tight text-white" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
            {app.name.replace("StudoX ", "")}
          </p>
          <p className="mt-0.5 truncate text-[9.5px] font-bold uppercase tracking-[0.14em]" style={{ color: `${app.accentA}bb` }}>{app.tagline}</p>
        </div>

        {/* launch hint — appears on hover */}
        <span
          className="pointer-events-none absolute bottom-2.5 z-20 rounded-full px-3 py-1 text-[9px] font-black uppercase tracking-wider text-white opacity-0 transition-all duration-300 group-hover:bottom-3 group-hover:opacity-100"
          style={{ background: `linear-gradient(90deg, ${app.accentA}, ${app.accentB})`, boxShadow: `0 0 18px ${app.accentA}55` }}
        >
          {app.comingSoon ? "Vormerken" : app.external ? "Öffnen ↗" : "Starten →"}
        </span>
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE — the app drawer
   ═══════════════════════════════════════════════════════════════════════════ */
function AppSheet({ app, onLaunch, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div className="sheet-up w-full max-w-md rounded-t-[28px] border-t border-x px-6 pt-3 pb-8" style={{ background: "rgba(11,10,26,0.98)", borderColor: `${app.accentA}40`, boxShadow: `0 -20px 80px ${app.accentA}22` }} onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto w-10 h-1 rounded-full bg-white/15 mb-5" />
        <div className="flex items-center gap-4">
          {hasSprite(app.id) ? (
            <div className="relative grid place-items-center shrink-0" style={{ width: 104, height: 104 }}>
              <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 70% 55% at 50% 62%, ${app.accentA}2a, transparent 70%)` }} />
              <AppSprite appId={app.id} size={100} playing warm style={{ filter: `drop-shadow(0 12px 24px ${app.accentA}55)` }} />
              <div className="pointer-events-none absolute bottom-0.5 h-3.5 w-2/3 rounded-full blur-md" style={{ background: `radial-gradient(ellipse, ${app.accentA}55, transparent 70%)` }} />
            </div>
          ) : (
            <div style={{ filter: `drop-shadow(0 0 18px ${app.accentA}55)` }}><AppGlyph app={app} size={68} glow /></div>
          )}
          <div className="min-w-0">
            <h3 className="text-xl font-black text-white leading-tight">{app.name}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest mt-0.5" style={{ color: `${app.accentA}dd` }}>{app.tagline}</p>
          </div>
        </div>
        <p className="text-[13px] text-gray-400 leading-relaxed mt-4">{app.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {app.badges.map((b) => (
            <span key={b} className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border" style={{ borderColor: `${app.accentA}28`, background: `${app.accentA}0c`, color: `${app.accentA}cc` }}>{b}</span>
          ))}
        </div>
        <button
          onClick={() => { onClose(); onLaunch(); }}
          className="mt-6 w-full py-4 rounded-2xl font-black text-[15px] text-white transition-transform active:scale-[0.98]"
          style={{ background: app.comingSoon ? "rgba(255,255,255,0.06)" : `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`, boxShadow: app.comingSoon ? "none" : `0 0 30px ${app.accentA}55`, border: app.comingSoon ? `1px solid ${app.accentA}40` : "none", color: app.comingSoon ? app.accentA : "#fff" }}
        >
          {app.comingSoon ? "🔔 Benachrichtigen lassen" : app.external ? "Öffnen ↗" : "Starten →"}
        </button>
      </div>
    </div>
  );
}

function MobileDrawer({ apps, cat, setCatId, onLaunch, header, banner, footer }) {
  const [sheetApp, setSheetApp] = useState(null);
  const catApps = cat.appIds.map((id) => apps.find((a) => a.id === id)).filter(Boolean);

  return (
    <div className="absolute inset-0 flex flex-col">
      {header}

      {/* icon grid */}
      <div className="flex-1 overflow-y-auto px-5 pb-32 pt-2">
        <div key={cat.id} className="stage-swap">
          <div className="flex items-center gap-2.5 mb-4 mt-2">
            <span className="text-2xl" style={{ filter: `drop-shadow(0 0 10px ${cat.color}88)` }}>{cat.icon}</span>
            <div>
              <p className="text-base font-black text-white leading-none">{cat.label}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest mt-1" style={{ color: `${cat.color}cc` }}>{cat.sub}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-3 gap-y-5 min-[420px]:grid-cols-4">
            {catApps.map((app, i) => (
              <button key={app.id} onClick={() => setSheetApp(app)} className="flex flex-col items-center gap-2 group" style={{ animation: `fade-in .4s ease ${i * 0.04}s both` }}>
                <div
                  className="grid place-items-center rounded-[22px] transition-transform duration-150 active:scale-90"
                  style={{
                    width: 74, height: 74,
                    background: `radial-gradient(ellipse at 30% 20%, ${app.accentA}30, rgba(10,10,22,0.9) 70%)`,
                    border: `1.5px solid ${app.accentA}45`,
                    boxShadow: `0 8px 24px rgba(0,0,0,0.45), 0 0 18px ${app.accentA}22`,
                    opacity: app.comingSoon ? 0.55 : 1,
                  }}
                >
                  {iconSrc(app.id) ? (
                    <img src={iconSrc(app.id)} alt="" className="object-contain" style={{ width: 52, height: 52, filter: `drop-shadow(0 4px 10px ${app.accentA}44)` }} loading="lazy" />
                  ) : (
                    <AppGlyph app={app} size={46} />
                  )}
                </div>
                <span className="text-[10.5px] font-bold text-white/80 leading-tight text-center max-w-[80px] truncate">
                  {app.name.replace("StudoX ", "")}
                </span>
                {app.comingSoon && <span className="text-[8px] font-black text-amber-400 -mt-1">BALD</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">{banner}</div>
        {footer}
      </div>

      {/* bottom category dock */}
      <div className="absolute inset-x-0 bottom-0 pb-safe" style={{ background: "linear-gradient(180deg, transparent, rgba(4,4,14,0.94) 30%)", paddingTop: 18 }}>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-4">
          {CATEGORIES.map((c) => {
            const active = c.id === cat.id;
            return (
              <button
                key={c.id}
                onClick={() => setCatId(c.id)}
                className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl shrink-0 transition-all duration-200 active:scale-95"
                style={{
                  background: active ? `linear-gradient(135deg, ${c.color}33, ${c.color}14)` : "rgba(255,255,255,0.05)",
                  border: `1.5px solid ${active ? `${c.color}66` : "rgba(255,255,255,0.08)"}`,
                  boxShadow: active ? `0 0 18px ${c.color}33` : "none",
                }}
              >
                <span className="text-base">{c.icon}</span>
                <span className="text-[11px] font-black" style={{ color: active ? "#fff" : "rgba(255,255,255,0.5)" }}>{c.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {sheetApp && <AppSheet app={sheetApp} onLaunch={() => onLaunch(sheetApp)} onClose={() => setSheetApp(null)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE BRIDGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function CoreBridge({
  apps, user, isAuthenticated, backendUrl, helpUrl, logo,
  onLaunch, onLogin, onLogout, onDashboard, onTeacherDash, banner,
}) {
  const vp = useViewport();
  const [catId, setCatId] = useState(CATEGORIES[0].id);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dossierGate, setDossierGate] = useState(false);
  const cat = CATEGORIES.find((c) => c.id === catId) || CATEGORIES[0];
  const catApps = cat.appIds.map((id) => apps.find((a) => a.id === id)).filter(Boolean);

  /* global hotkeys: ⌘K palette · 1–8 organ switch */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((v) => !v); return; }
      if (paletteOpen) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= CATEGORIES.length) setCatId(CATEGORIES[n - 1].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  /* shared footer */
  const footer = (
    <div className="flex flex-col items-center gap-3 mt-8 mb-4 opacity-80">
      <div className="flex flex-wrap justify-center items-center gap-1.5">
        {LINKS.map(({ label, href, color }) => (
          <a key={label} href={href} className="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all hover:scale-105" style={{ color, background: `${color}0e`, border: `1px solid ${color}24`, textDecoration: "none" }}>
            <span style={{ color: "rgba(255,255,255,0.3)" }}>studox.</span>{label}
          </a>
        ))}
      </div>
      <p className="text-[9px] font-mono text-gray-700 tracking-widest">STUDOX OS v3.0 — DEINE WELTEN · © {new Date().getFullYear()} StudoX</p>
    </div>
  );

  /* shared user cluster (header right) */
  const userCluster = (
    <div className="flex items-center gap-1.5">
      {onTeacherDash && (
        <button onClick={onTeacherDash} className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-105" style={{ borderColor: "rgba(6,182,212,0.4)", color: "#67e8f9", background: "rgba(6,182,212,0.1)" }}>
          🎓 Lehrer-Dashboard
        </button>
      )}
      {onDashboard && (
        <button onClick={onDashboard} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-105" style={{ borderColor: "rgba(139,92,246,0.4)", color: "#c084fc", background: "rgba(139,92,246,0.1)" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          <span className="hidden sm:inline">Dashboard</span>
        </button>
      )}
      {isAuthenticated && user ? (
        <>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border" style={{ borderColor: "rgba(139,92,246,0.2)", background: "rgba(139,92,246,0.07)" }}>
            <span className="text-sm leading-none">
              {user.avatarUrl ? <img src={`${backendUrl}${user.avatarUrl}`} alt="" className="w-5 h-5 rounded-lg object-cover inline-block" /> : (user.avatar || "🦊")}
            </span>
            <span className="hidden sm:inline text-xs font-bold text-violet-300 max-w-[110px] truncate">{user.name}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ boxShadow: "0 0 4px #22c55e" }} />
          </div>
          <button onClick={onLogout} title="Abmelden" className="px-2.5 py-1.5 rounded-xl border text-xs font-bold transition-all hover:scale-105" style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", color: "#fca5a5" }}>
            ⎋
          </button>
        </>
      ) : (
        <button onClick={onLogin} className="px-4 py-1.5 rounded-xl text-xs font-black text-white transition-all hover:scale-105" style={{ background: "linear-gradient(135deg, #8b5cf6, #06b6d4)", boxShadow: "0 0 16px rgba(139,92,246,0.35)" }}>
          Anmelden
        </button>
      )}
    </div>
  );

  /* ── MOBILE ─────────────────────────────────────────────────────────────── */
  if (vp === "mobile") {
    return (
      <div className="bridge-in absolute inset-0">
        <MobileDrawer
          apps={apps} cat={cat} setCatId={setCatId} onLaunch={onLaunch} banner={banner} footer={footer}
          header={
            <div className="pt-safe px-5 pt-5 pb-3 flex items-center gap-3">
              <div style={{ filter: "drop-shadow(0 0 12px rgba(139,92,246,0.6))" }}>{logo}</div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-black text-white leading-none">StudoX Core</p>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-violet-300/60 mt-1">Command Bridge</p>
              </div>
              <a href={helpUrl} title="Hilfe" className="w-9 h-9 grid place-items-center rounded-xl text-base" style={{ background: "linear-gradient(135deg, #f43f5e, #ec4899)", boxShadow: "0 0 14px rgba(244,63,94,0.35)" }}>💗</a>
              <button onClick={() => setPaletteOpen(true)} title="Suchen" className="w-9 h-9 grid place-items-center rounded-xl border text-gray-300" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              </button>
              {userCluster}
            </div>
          }
        />
        {paletteOpen && <CommandPalette apps={apps} onLaunch={onLaunch} onClose={() => setPaletteOpen(false)} />}
      </div>
    );
  }

  /* ── BRIDGE (tablet / desktop / wide) ───────────────────────────────────── */
  const compactSpine = vp === "tablet";
  const wide = vp === "wide";

  return (
    <div className="bridge-in absolute inset-0 flex flex-col">
      <style>{`
        @keyframes gate-in { from { opacity: 0; transform: translateY(14px) scale(0.96); } to { opacity: 1; transform: none; } }
        .gate-ring { will-change: transform; }
        .gate-ring:hover { filter: brightness(1.12); }
        .gate-tile .gate-glyph { animation: float 4.2s ease-in-out infinite; }
      `}</style>
      {/* ═ header bar ═ */}
      <div className="shrink-0 flex items-center gap-4 px-5 h-[64px] border-b" style={{ background: "rgba(6,5,18,0.72)", backdropFilter: "blur(22px)", borderColor: "rgba(139,92,246,0.14)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <div style={{ filter: "drop-shadow(0 0 14px rgba(139,92,246,0.6))" }}>{logo}</div>
          <div className="hidden md:block">
            <p className="text-[15px] font-black text-white leading-none tracking-tight">StudoX Core</p>
            <p className="text-[8.5px] font-black uppercase tracking-[0.34em] text-violet-300/55 mt-1">Deine Welten · v3.0</p>
          </div>
        </div>

        {/* ⌘K trigger */}
        <button
          onClick={() => setPaletteOpen(true)}
          className="flex-1 max-w-md mx-auto flex items-center gap-3 px-4 py-2 rounded-2xl border text-left transition-all hover:scale-[1.015] group"
          style={{ borderColor: "rgba(139,92,246,0.22)", background: "rgba(255,255,255,0.035)" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#8b5cf6" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <span className="flex-1 text-xs font-semibold text-gray-500 group-hover:text-gray-400 transition-colors truncate">App suchen und sofort starten…</span>
          <span className="text-[9px] font-black text-gray-500 border border-white/12 rounded-md px-1.5 py-0.5 shrink-0">⌘K</span>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <a href={helpUrl} className="core-soft-pulse flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black text-white transition-all hover:scale-105" style={{ background: "linear-gradient(135deg, #f43f5e, #ec4899)", boxShadow: "0 0 14px rgba(244,63,94,0.3)", textDecoration: "none" }} title="Vertrauliche Notfallhilfe">
            💗 <span className="hidden lg:inline">Hilfe</span>
          </a>
          {userCluster}
        </div>
      </div>

      {/* ═ body: spine + stage ═ */}
      <div className="flex-1 flex min-h-0">
        {/* organ spine */}
        <div className={`shrink-0 flex flex-col border-r overflow-y-auto scrollbar-hide ${compactSpine ? "w-[68px] px-2" : wide ? "w-[300px] px-4" : "w-[264px] px-3"} py-4 gap-1`} style={{ background: "rgba(6,5,17,0.6)", backdropFilter: "blur(18px)", borderColor: "rgba(139,92,246,0.1)" }}>
          {!compactSpine && <p className="px-2 pb-2 text-[9px] font-black uppercase tracking-[0.3em] text-gray-600">Deine Welten</p>}
          {CATEGORIES.map((c, i) => (
            <Fragment key={c.id}>
              {c.dark && (
                <div className="my-2 flex items-center gap-2 px-2" aria-hidden>
                  <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, rgba(229,39,58,0.4), transparent)" }} />
                  {!compactSpine && <span className="text-[8px] font-black uppercase tracking-[0.3em] text-red-400/50">Beschränkt</span>}
                  <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, rgba(229,39,58,0.4), transparent)" }} />
                </div>
              )}
              <SpineItem cat={c} index={i} active={c.id === catId} compact={compactSpine} count={c.appIds.length} onPick={() => setCatId(c.id)} />
            </Fragment>
          ))}
          <div className="mt-auto pt-4">
            {!compactSpine && (
              <div className="px-2 py-3 rounded-2xl border text-center" style={{ borderColor: "rgba(139,92,246,0.14)", background: "rgba(139,92,246,0.04)" }}>
                <p className="text-[9px] font-black uppercase tracking-widest text-violet-300/60">Ecosystem v3.0</p>
                <p className="text-[8.5px] text-gray-600 mt-1 font-mono">Tasten 1–8 · ⌘K Suche</p>
              </div>
            )}
            {/* the quiet door — almost invisible until you know it's there */}
            <button
              onClick={() => setDossierGate(true)}
              title="…"
              className="mx-auto mt-3 block px-3 py-1.5"
              style={{ background: "transparent", border: "none", cursor: "pointer", opacity: 0.12, transition: "opacity 1.2s ease" }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.55)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.12)}
            >
              <span className="font-mono text-[10px] tracking-[0.4em]" style={{ color: "#8b93a3" }}>▪ ▪ ▪</span>
            </button>
          </div>
        </div>

        {/* stage */}
        <div className="flex-1 min-w-0 overflow-y-auto relative">
          {/* dark-side ambience — the room shifts when you enter the dark world */}
          {cat.dark && (
            <div className="pointer-events-none absolute inset-0 z-0" style={{ background: "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(229,39,58,0.10), transparent 60%)" }} />
          )}
          <div key={cat.id} className="stage-swap relative z-10 px-6 lg:px-9 py-7 max-w-[1600px] mx-auto">
            {/* Welt-Header */}
            <div className="flex items-center gap-4 mb-3">
              <span className="grid place-items-center rounded-3xl text-3xl shrink-0" style={{ width: 66, height: 66, background: `${cat.color}16`, border: `1.5px solid ${cat.color}44`, filter: `drop-shadow(0 0 18px ${cat.color}55)` }}>{cat.icon}</span>
              <div className="min-w-0">
                <p className="text-[9px] font-black uppercase tracking-[0.4em]" style={{ color: `${cat.color}99` }}>Welt {String(CATEGORIES.findIndex((c) => c.id === cat.id) + 1).padStart(2, "0")} / {String(CATEGORIES.length).padStart(2, "0")}</p>
                <h2 className="text-3xl font-black tracking-tight leading-none mt-1" style={{ background: `linear-gradient(110deg, #fff, ${cat.color})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.7))" }}>
                  {cat.label}
                </h2>
                <p className="text-[11px] font-black uppercase tracking-[0.28em] mt-2" style={{ color: `${cat.color}cc` }}>{cat.sub} · {catApps.length} {catApps.length === 1 ? "App" : "Apps"}</p>
              </div>
              <div className="flex-1 h-px ml-2" style={{ background: `linear-gradient(90deg, ${cat.color}44, transparent)` }} />
            </div>

            {/* dark-side warning line */}
            {cat.dark && (
              <div className="mb-5 mt-4 flex items-center gap-2.5 rounded-2xl border px-4 py-2.5" style={{ borderColor: "rgba(229,39,58,0.35)", background: "rgba(229,39,58,0.06)" }}>
                <span className="text-sm">🕯️</span>
                <p className="text-[11px] font-bold text-red-200/80">Diese Welt behandelt ernste, belegte Realität. Zugang beschränkt, Inhalte können belasten — betreten ist eine bewusste Entscheidung.</p>
              </div>
            )}
            {!cat.dark && <div className="mb-5" />}

            {/* DAS GRID — eine Kachelform, alle gleich groß, füllt jede Breite */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${wide ? 220 : 196}px, 1fr))` }}>
              {catApps.map((app, i) => (
                <GateTile key={app.id} app={app} index={i} onLaunch={() => onLaunch(app)} />
              ))}
            </div>

            <div className="mt-10">{banner}</div>
            {footer}
          </div>
        </div>
      </div>

      {paletteOpen && <CommandPalette apps={apps} onLaunch={onLaunch} onClose={() => setPaletteOpen(false)} />}
      {dossierGate && <DossierGate />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DOSSIER GATE — the quiet door out of the bridge.
   An EXTRA-smooth darkweb-style handoff: the room dims to black over seconds,
   a single red pulse line drifts through, a mono whisper types itself — then
   the browser is already somewhere else. No flash, no bang. Barely a ripple.
   ═══════════════════════════════════════════════════════════════════════════ */
function DossierGate() {
  const [line, setLine] = useState("");
  useEffect(() => {
    const msg = "verbinde mit gesichertem knoten …";
    let i = 0;
    const type = setInterval(() => {
      i++;
      setLine(msg.slice(0, i));
      if (i >= msg.length) clearInterval(type);
    }, 46);
    const go = setTimeout(() => { window.location.href = "https://dossier.studox.eu"; }, 3400);
    return () => { clearInterval(type); clearTimeout(go); };
  }, []);

  return (
    <div className="fixed inset-0 z-[90] pointer-events-auto" style={{ cursor: "none" }}>
      <style>{`
        @keyframes dg-fade  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dg-line  { 0% { top: -4%; opacity: 0; } 12% { opacity: 0.5; } 88% { opacity: 0.5; } 100% { top: 104%; opacity: 0; } }
        @keyframes dg-hum   { 0%,100% { opacity: 0.035; } 50% { opacity: 0.07; } }
        @keyframes dg-text  { 0% { opacity: 0; } 100% { opacity: 0.65; } }
      `}</style>
      {/* the room dims — slow, even, inescapable */}
      <div className="absolute inset-0" style={{ background: "#020104", animation: "dg-fade 2.6s cubic-bezier(0.4,0,0.6,1) both" }} />
      {/* faint red static breathing in the black */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 70% 55% at 50% 60%, rgba(229,39,58,0.5), transparent 70%)",
        animation: "dg-hum 3s ease-in-out infinite, dg-fade 3s ease 0.8s both", opacity: 0,
      }} />
      {/* a single scan pulse drifting down */}
      <div className="absolute left-0 right-0 h-px" style={{
        background: "linear-gradient(90deg, transparent, rgba(229,39,58,0.6), transparent)",
        boxShadow: "0 0 18px rgba(229,39,58,0.35)",
        animation: "dg-line 2.8s cubic-bezier(0.45,0,0.55,1) 0.9s both",
      }} />
      {/* the whisper */}
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-mono text-[11px] tracking-[0.35em]" style={{ color: "#e5273a", animation: "dg-text 1.6s ease 1.1s both", textShadow: "0 0 14px rgba(229,39,58,0.4)" }}>
          {line}<span style={{ opacity: line.length % 2 ? 1 : 0 }}>_</span>
        </span>
      </div>
    </div>
  );
}
